import type { FC } from 'react';
import React, { useCallback, useContext, useEffect, useState, useMemo, useRef } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { useQuery } from '@apollo/react-hooks';

import { Box, Inline, Stack, xcss, Text } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import Image from '@atlaskit/image';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import Button, { IconButton } from '@atlaskit/button/new';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import VidPlayIcon from '@atlaskit/icon/glyph/vid-play';

import { usePopupDwellTime } from '@atlassian/popup-dwell-time';

import { Attribution, TransparentErrorBoundary } from '@confluence/error-boundary';
import { SPAViewContext } from '@confluence/spa-view-context';
import { isErrorMarkedAsHandled, markErrorAsHandled } from '@confluence/graphql';
import { useIsCompanyHubEnabled } from '@confluence/company-hub-utils/entry-points/useIsCompanyHubEnabled';
import { useSpaceKey } from '@confluence/space-utils';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager';

import leftIllustration from '../images/Illustration.svg';
import atlassianIntelligenceIconLight from '../images/ai-icon-light.svg';
import atlassianIntelligenceIconDark from '../images/ai-icon-dark.svg';
import analyticsIconLight from '../images/analytics-icon-light.svg';
import analyticsIconDark from '../images/analytics-icon-dark.svg';
import automationIconLight from '../images/automation-icon-light.svg';
import automationIconDark from '../images/automation-icon-dark.svg';
import companyHubIconLight from '../images/company-hub-icon-light.svg';
import companyHubIconDark from '../images/company-hub-icon-dark.svg';
import contentManagerIconLight from '../images/content-manager-icon-light.svg';
import contentManagerIconDark from '../images/content-manager-icon-dark.svg';
import missionControlIconLight from '../images/mission-control-icon-light.svg';
import missionControlIconDark from '../images/mission-control-icon-dark.svg';
import teamCalendarsIconLight from '../images/team-calendars-icon-light.svg';
import teamCalendarsIconDark from '../images/team-calendars-icon-dark.svg';
import whiteboardIconLight from '../images/whiteboard-icon-light.svg';
import whiteboardIconDark from '../images/whiteboard-icon-dark.svg';
import { OnboardingManualTrialModalQuery } from '../OnboardingManualTrialModalQuery.graphql';
import type { OnboardingManualTrialModalQuery as OnboardingManualTrialModalQueryType } from '../__types__/OnboardingManualTrialModalQuery';

import type { ListItemProps } from './ListItem';
import { ListItem } from './ListItem';
import { PremiumFeatureDeeplinks } from './PremiumFeatureDeeplinks';
import { LearnMoreVideoModal } from './LearnMoreVideoModal';

const i18n = defineMessages({
	modalTitle: {
		id: 'premium-trial.onboarding.mt.modal.title',
		description: 'Title for modal',
		defaultMessage: 'Welcome to your Premium trial',
	},
	modalDescription: {
		id: 'premium-trial.onboarding.mt.modal.description',
		description: 'Description for modal',
		defaultMessage: 'Get started by exploring our 5 most popular features.',
	},
	videoButtonText: {
		id: 'premium-trial.onboarding.mt.video-button.text',
		description: 'CTA button text that opens a video',
		defaultMessage: 'Watch a summary of Premium',
	},
	/* features */
	aiTitle: {
		id: 'premium-trial.onboarding.mt.ai.title',
		description: 'Title for AI feature',
		defaultMessage: 'Atlassian Intelligence (AI)',
	},
	aiDesciption: {
		id: 'premium-trial.onboarding.mt.ai.description',
		description: 'Description for AI feature',
		defaultMessage:
			'Work smarter by leveraging AI-powered editing experiences to create and summarize content.',
	},
	aiAction: {
		id: 'premium-trial.onboarding.mt.ai.action',
		description: 'Action for AI feature',
		defaultMessage: 'Discover the power of AI',
	},
	companyHubTitle: {
		id: 'premium-trial.onboarding.mt.company-hub.title',
		description: 'Title for company hub feature',
		defaultMessage: 'Company Hub',
	},
	companyHubDescription: {
		id: 'premium-trial.onboarding.mt.company-hub.description',
		description: 'Description for company hub feature',
		defaultMessage:
			'Help your teams stay connected with a centralized intranet for key announcements and resources.',
	},
	companyHubAction: {
		id: 'premium-trial.onboarding.mt.company-hub.action',
		description: 'Action for company hub feature',
		defaultMessage: 'Customize my company hub',
	},
	automationTitle: {
		id: 'premium-trial.onboarding.mt.automation.title',
		description: 'Title for automation feature',
		defaultMessage: 'Automation',
	},
	automationDescription: {
		id: 'premium-trial.onboarding.mt.automation.description',
		description: 'Description for automation feature',
		defaultMessage:
			'Save time by creating rules to change a page status, create a Jira task, and more.',
	},
	automationAction: {
		id: 'premium-trial.onboarding.mt.automation.action',
		description: 'Action for automation feature',
		defaultMessage: 'Build an automation rule',
	},
	missionControlTitle: {
		id: 'premium-trial.onboarding.mt.mission-control.title',
		description: 'Title for mission control feature',
		defaultMessage: 'Mission control',
	},
	missionControlDescription: {
		id: 'premium-trial.onboarding.mt.mission-control.description',
		description: 'Description for mission control feature',
		defaultMessage:
			'Boost your team’s productivity with insights and controls for data-driven improvements.',
	},
	missionControlAction: {
		id: 'premium-trial.onboarding.mt.mission-control.action',
		description: 'Action for mission control feature',
		defaultMessage: 'Go to mission control',
	},
	contentManagerTitle: {
		id: 'premium-trial.onboarding.mt.content-manager.title',
		description: 'Title for content manager feature',
		defaultMessage: 'Content manager',
	},
	contentManagerDescription: {
		id: 'premium-trial.onboarding.mt.content-manager.description',
		description: 'Description for content manager feature',
		defaultMessage:
			'Keep content organized and make bulk changes across hundreds of pages simultaneously.',
	},
	contentManagerAction: {
		id: 'premium-trial.onboarding.mt.content-manager.action',
		description: 'Action for content manager feature',
		defaultMessage: 'View my content manager',
	},
	teamCalendarsTitle: {
		id: 'premium-trial.onboarding.mt.team-calendars.title',
		description: 'Title for team calendars feature',
		defaultMessage: 'Team calendars',
	},
	teamCalendarsDescription: {
		id: 'premium-trial.onboarding.mt.team-calendars.description',
		description: 'Description for team calendars feature',
		defaultMessage: 'Keep one single source of truth for your team’s schedules and due dates.',
	},
	teamCalendarsAction: {
		id: 'premium-trial.onboarding.mt.team-calendars.action',
		description: 'Action for team calendars feature',
		defaultMessage: 'Go to team calendars',
	},
	unlimitedWhiteBoardsTitle: {
		id: 'premium-trial.onboarding.mt.unlimited-whiteboards.title',
		description: 'Title for unlimited whiteboards feature',
		defaultMessage: 'Whiteboards and Jira without limits',
	},
	unlimitedWhiteBoardsDescription: {
		id: 'premium-trial.onboarding.mt.unlimited-whiteboards.description',
		description: 'Description for unlimited whiteboards feature',
		defaultMessage:
			'Brainstorm without boundaries and turn ideas into Jira tasks from one single location.',
	},
	unlimitedWhiteBoardsAction: {
		id: 'premium-trial.onboarding.mt.unlimited-whiteboards.action',
		description: 'Action for unlimited whiteboards feature',
		defaultMessage: 'Create a whiteboard',
	},
	analyticsTitle: {
		id: 'premium-trial.onboarding.mt.analytics.title',
		description: 'Title for analytics feature',
		defaultMessage: 'Analytics',
	},
	analyticsDescription: {
		id: 'premium-trial.onboarding.mt.analytics.description',
		description: 'Description for analytics feature',
		defaultMessage: 'See user engagement and views at the site, space, and page level.',
	},
	analyticsAction: {
		id: 'premium-trial.onboarding.mt.analytics.action',
		description: 'Action for analytics feature',
		defaultMessage: 'View analytics',
	},
});

const imageContainerStyles = xcss({
	width: '145',
	minInlineSize: 'max-content',
});

type ModalProps = {
	onClose?: () => void;
	stop?: () => void;
};

enum Features {
	AI = 'atlassianIntelligence',
	TEAM_CALENDARS = 'teamCalendars',
	COMPANY_HUB = 'companyHub',
	UNLIMITED_WHITEBOARDS = 'unlimitedWhiteboards',
	AUTOMATION = 'automation',
	MISSION_CONTROL = 'missionControl',
	CONTENT_MANAGER = 'contentManager',
	ANALYTICS = 'analytics',
}

const featuresMapper: Array<
	Omit<ListItemProps, 'link' | 'key'> & {
		key: Features;
		showToAdmin?: boolean;
		showToEndUser?: boolean;
	}
> = [
	{
		key: Features.AI,
		title: i18n.aiTitle,
		bodyText: i18n.aiDesciption,
		linkText: i18n.aiAction,
		img: atlassianIntelligenceIconLight,
		darkImg: atlassianIntelligenceIconDark,
		showToAdmin: true,
		showToEndUser: true,
	},
	{
		key: Features.TEAM_CALENDARS,
		title: i18n.teamCalendarsTitle,
		bodyText: i18n.teamCalendarsDescription,
		linkText: i18n.teamCalendarsAction,
		img: teamCalendarsIconLight,
		darkImg: teamCalendarsIconDark,
		showToAdmin: false,
		showToEndUser: true,
	},
	{
		key: Features.COMPANY_HUB,
		title: i18n.companyHubTitle,
		bodyText: i18n.companyHubDescription,
		linkText: i18n.companyHubAction,
		img: companyHubIconLight,
		darkImg: companyHubIconDark,
		showToAdmin: true,
		showToEndUser: false,
	},
	{
		key: Features.UNLIMITED_WHITEBOARDS,
		title: i18n.unlimitedWhiteBoardsTitle,
		bodyText: i18n.unlimitedWhiteBoardsDescription,
		linkText: i18n.unlimitedWhiteBoardsAction,
		img: whiteboardIconLight,
		darkImg: whiteboardIconDark,
		showToAdmin: false,
		showToEndUser: true,
	},
	{
		key: Features.AUTOMATION,
		title: i18n.automationTitle,
		bodyText: i18n.automationDescription,
		linkText: i18n.automationAction,
		img: automationIconLight,
		darkImg: automationIconDark,
		showToAdmin: true,
		showToEndUser: true,
	},
	{
		key: Features.MISSION_CONTROL,
		title: i18n.missionControlTitle,
		bodyText: i18n.missionControlDescription,
		linkText: i18n.missionControlAction,

		img: missionControlIconLight,
		darkImg: missionControlIconDark,
		showToAdmin: true,
		showToEndUser: false,
	},
	{
		key: Features.CONTENT_MANAGER,
		title: i18n.contentManagerTitle,
		bodyText: i18n.contentManagerDescription,
		linkText: i18n.contentManagerAction,
		img: contentManagerIconLight,
		darkImg: contentManagerIconDark,
		showToAdmin: true,
		showToEndUser: false,
	},
	{
		key: Features.ANALYTICS,
		title: i18n.analyticsTitle,
		bodyText: i18n.analyticsDescription,
		linkText: i18n.analyticsAction,
		img: analyticsIconLight,
		darkImg: analyticsIconDark,
		showToAdmin: false,
		showToEndUser: true,
	},
];

export const MODAL_NAME = 'premiumOnboardingManualTrialModal';

const modalClosedLocalStorageKey = 'premium.trial.onboarding.mt.modal.closed';

export const OnboardingManualTrialModal: FC<ModalProps> = ({ onClose, stop }) => {
	const [isOpen, setIsOpen] = useState(true);
	const [isLearnMoreVideoOpen, setIsLearnMoreVideoIsOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isSiteAdmin } = useContext(SPAViewContext);
	const { isCompanyHubEntryPointEnabled } = useIsCompanyHubEnabled();
	const currentSpaceKey = useSpaceKey();

	const { data: { personalSpace, popularSpaces, starredSpace, recentSpaces } = {}, error } =
		useQuery<OnboardingManualTrialModalQueryType>(OnboardingManualTrialModalQuery);
	const personalSpaceKey =
		(personalSpace?.confluence?.hasPersonalSpace && personalSpace?.confluence?.space?.key) ||
		undefined;
	const popularSpace = popularSpaces?.nodes.find((node) => node.content?.space?.type !== 'system');
	const popularSpaceKey = popularSpace?.content?.space?.key || undefined;
	const starredSpaceKey = starredSpace?.nodes?.[0]?.key || undefined;
	const recentSpaceKey = recentSpaces?.[0]?.key || undefined;

	if (error && !isErrorMarkedAsHandled(error)) {
		markErrorAsHandled(error);
	}

	const premiumFeatureDeeplinks = useMemo(() => {
		return new PremiumFeatureDeeplinks({
			personalSpaceKey,
			popularSpaceKey,
			starredSpaceKey,
			recentSpaceKey,
			currentSpaceKey: currentSpaceKey || undefined,
		});
	}, [personalSpaceKey, popularSpaceKey, starredSpaceKey, recentSpaceKey, currentSpaceKey]);

	const featureLinksMapper: Record<Features, string> = useMemo(() => {
		return {
			[Features.AI]: premiumFeatureDeeplinks.getAi(),
			[Features.TEAM_CALENDARS]: premiumFeatureDeeplinks.getTeamCalendars(),
			[Features.COMPANY_HUB]: premiumFeatureDeeplinks.getCompanyHub({
				isCompanyHubEnabled: isCompanyHubEntryPointEnabled,
			}),
			[Features.UNLIMITED_WHITEBOARDS]: premiumFeatureDeeplinks.getWhiteboard({
				source: MODAL_NAME,
			}),
			[Features.AUTOMATION]: isSiteAdmin
				? premiumFeatureDeeplinks.getAutomationAdmin()
				: premiumFeatureDeeplinks.getAutomationEndUser(),
			[Features.MISSION_CONTROL]: premiumFeatureDeeplinks.getMissionControl(),
			[Features.CONTENT_MANAGER]: premiumFeatureDeeplinks.getContentManager(),
			[Features.ANALYTICS]: premiumFeatureDeeplinks.getAnalytics(),
		};
	}, [isCompanyHubEntryPointEnabled, isSiteAdmin, premiumFeatureDeeplinks]);

	const featuresToShow = featuresMapper.filter(
		(feature) => (isSiteAdmin && feature.showToAdmin) || (!isSiteAdmin && feature.showToEndUser),
	);

	const modalRef = useRef<HTMLDivElement | null>(null);
	const { getDwellTime } = usePopupDwellTime(modalRef);

	const hasLinkEventFiredWithDwellTime = useRef<boolean>(false);

	const closeModal = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'ptoManualTrialModalCloseButton',
				source: MODAL_NAME,
				attributes: {
					dwellTime: getDwellTime(),
				},
			},
		}).fire();
		setIsOpen(false);
		onClose?.();
	}, [createAnalyticsEvent, getDwellTime, onClose]);

	const openLearnMoreVideo = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'premiumVideoModalButton',
				source: MODAL_NAME,
				attributes: {
					dwellTime: getDwellTime(),
				},
			},
		}).fire();

		setIsLearnMoreVideoIsOpen(true);
	}, [createAnalyticsEvent, getDwellTime]);

	const closeLearnMoreVideo = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'premiumVideoModalCloseButton',
				source: MODAL_NAME,
			},
		}).fire();

		setIsLearnMoreVideoIsOpen(false);
	}, [createAnalyticsEvent]);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: MODAL_NAME,
				attributes: {
					isSiteAdmin,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, isSiteAdmin]);

	const onLinkClick = useCallback(
		(feature: Features) => {
			let dwellTime = {};
			if (!hasLinkEventFiredWithDwellTime.current) {
				dwellTime = { dwellTime: getDwellTime() };
				hasLinkEventFiredWithDwellTime.current = true;
			}

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'link',
					actionSubjectId: 'premiumFeatureDeeplink',
					source: MODAL_NAME,
					attributes: {
						feature,
						isSiteAdmin,
						...dwellTime,
					},
				},
			}).fire();
			stop?.();
			localStorage.setItem(modalClosedLocalStorageKey, 'true');
		},
		[createAnalyticsEvent, isSiteAdmin, stop, getDwellTime],
	);

	if (localStorage.getItem(modalClosedLocalStorageKey)) {
		return null;
	}

	return (
		<TransparentErrorBoundary attribution={Attribution.EDITIONS}>
			<ModalTransition>
				{isLearnMoreVideoOpen && <LearnMoreVideoModal onClose={closeLearnMoreVideo} />}
			</ModalTransition>
			<ModalTransition>
				{isOpen && (
					<Modal
						/* eslint-disable-next-line jsx-a11y/no-autofocus */
						autoFocus={false}
						width={752}
						shouldScrollInViewport
					>
						<Inline ref={modalRef} alignBlock="stretch">
							<Box xcss={imageContainerStyles}>
								<Image
									src={leftIllustration}
									srcDark={leftIllustration}
									css={{
										objectFit: 'cover',
										minHeight: '100%',
										width: '168px',
										height: '400px !important',
										top: '-20px',
										left: '-23px',
									}}
									alt="Premium trial onboarding modal image"
								/>
							</Box>
							<Box padding="space.300">
								<Inline alignInline="end">
									<IconButton
										appearance="subtle"
										icon={CrossIcon}
										onClick={closeModal}
										label="Close Modal"
									/>
								</Inline>
								<Box paddingInline="space.200">
									<Stack space="space.300">
										<Stack space="space.100">
											<Heading size="medium" as="h1">
												<FormattedMessage {...i18n.modalTitle} />
											</Heading>
											<Text as="p">
												<FormattedMessage {...i18n.modalDescription} />
											</Text>
										</Stack>
										<Stack space="space.300">
											{featuresToShow.map((feature) => (
												<ListItem
													key={feature.key}
													bodyText={feature.bodyText}
													linkText={feature.linkText}
													link={featureLinksMapper[feature.key]}
													darkImg={feature.darkImg}
													img={feature.img}
													title={feature.title}
													onLinkClick={() => onLinkClick(feature.key)}
												/>
											))}
										</Stack>
										<Inline alignInline="end">
											<Button iconBefore={VidPlayIcon} onClick={openLearnMoreVideo}>
												<FormattedMessage {...i18n.videoButtonText} />
											</Button>
										</Inline>
									</Stack>
								</Box>
							</Box>
						</Inline>
					</Modal>
				)}
			</ModalTransition>
		</TransparentErrorBoundary>
	);
};
